import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { HeroPlannerDebugPreSendCheck } from '~/hooks/apis/customer-communication/useHeroPlannerScheduleDebug';

import { HeroPlannerDebugRow, HeroPlannerDebugSummary } from '../../utils/heroPlannerDebugUtils';

import HeroPlannerDebugPreSendCheckChip from './HeroPlannerDebugPreSendCheckChip';
import HeroPlannerDebugPreSendCheckResult from './HeroPlannerDebugPreSendCheckResult';
import HeroPlannerDebugResultRow from './HeroPlannerDebugResultRow';

interface Props {
  brand: string;
  countryGroupName: string;
  /**
   * YYYY-MM-DD
   */
  sendDate: string;
  /** Counts for every row in the group, so the header stays accurate when the table is filtered */
  summary: HeroPlannerDebugSummary;
  /** Rows to show in the table, already filtered and sorted */
  visibleRows: Array<HeroPlannerDebugRow>;
  preSendChecks: Array<HeroPlannerDebugPreSendCheck>;
  offerId: string;
  onViewResponse: (row: HeroPlannerDebugRow) => void;
  onCopyUrl: (url: string) => void;
  onRetry: (targetKey: string) => void;
}

function HeroPlannerDebugCountryGroupSection({
  brand,
  countryGroupName,
  sendDate,
  summary,
  visibleRows,
  preSendChecks,
  offerId,
  onViewResponse,
  onCopyUrl,
  onRetry,
}: Props) {
  return (
    <Accordion defaultExpanded disableGutters variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" width="100%">
          <Typography variant="h6" component="h3">
            {countryGroupName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {summary.total} segments
          </Typography>
          {preSendChecks.map((check) => (
            <HeroPlannerDebugPreSendCheckChip key={check.segment ?? 'all'} check={check} />
          ))}
          <Stack direction="row" gap={1} ml="auto" mr={1} alignItems="center" flexWrap="wrap">
            {summary.loading > 0 && <CircularProgress size={16} aria-label={`${summary.loading} still running`} />}
            {summary.failed > 0 && <Chip size="small" color="error" label={`${summary.failed} failing`} />}
            {summary.regenerating > 0 && (
              <Chip size="small" color="warning" label={`${summary.regenerating} regenerating`} />
            )}
            {summary.ok > 0 && <Chip size="small" color="success" variant="outlined" label={`${summary.ok} passing`} />}
            {summary.heroOnly > 0 && (
              <Chip size="small" color="info" variant="outlined" label={`${summary.heroOnly} hero row only`} />
            )}
            {summary.skipped > 0 && <Chip size="small" variant="outlined" label={`${summary.skipped} never sent`} />}
            {offerId && summary.withOffer > 0 && (
              <Chip size="small" color="info" label={`Offer in ${summary.withOffer}`} />
            )}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Stack gap={1} px={2} pb={1}>
          {preSendChecks.map((check) => (
            <HeroPlannerDebugPreSendCheckResult key={check.segment ?? 'all'} check={check} />
          ))}
        </Stack>
        {visibleRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" px={2} pb={2}>
            No segments match the current filter.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label={`${countryGroupName} segment results`}>
              <TableHead>
                <TableRow>
                  <TableCell>Result</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>State / City</TableCell>
                  <TableCell>Segment</TableCell>
                  <TableCell>Matched row</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell align="right">Time</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => (
                  <HeroPlannerDebugResultRow
                    key={row.target.key}
                    row={row}
                    brand={brand}
                    sendDate={sendDate}
                    offerId={offerId}
                    onViewResponse={onViewResponse}
                    onCopyUrl={onCopyUrl}
                    onRetry={onRetry}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default HeroPlannerDebugCountryGroupSection;
